import { Injectable } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import { ObjectDrawerFilter } from 'app/component-library/object-drawer/object-drawer-filter/object-drawer-filter';
import { ObjectDrawerFilterValueType } from 'app/component-library/object-drawer/object-drawer-filter/object-drawer-filter-value-type';
import { ObjectDrawerParameters } from 'app/component-library/object-drawer/object-drawer-parameters';
import { LookupDisplayValue } from 'app/viewmodels/static-lookups/lookup-display-value';
import * as _ from 'lodash';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BackendService } from '../../core/backend.service';
import { StaticLookup } from '../../viewmodels/static-lookup';
import { StaticLookupAsync } from '../../viewmodels/static-lookup-async';
import { LookupItem } from '../../viewmodels/static-lookups/lookup-item';
import { BackendPaginationService } from '../backend-pagination.service';
import { CONFIG } from '../config';

@Injectable()
export class StaticLookupService {

  constructor(private backendService: BackendService, private backendPaginationService: BackendPaginationService, private i18NextPipe: I18NextPipe) { }

  public getById(lookupId: string, insertBlank?: boolean): Observable<LookupItem[]> {
    return this.getUnsortedById(lookupId, insertBlank).pipe(
      map(result => {
        const lookupItems = this.prependBlankIfRequired(result, insertBlank);
        return _.sortBy(lookupItems, item => item.value);
      }));
  }

  public getUnsortedById(lookupId: string, insertBlank?: boolean): Observable<LookupItem[]> {
    return this.backendService.getById<StaticLookup>(StaticLookup, lookupId).pipe(
      map(result => this.prependBlankIfRequired(result.items, insertBlank)));
  }

  public getByIdAsync(lookupId: string, insertBlank?: boolean): Observable<LookupItem[]> {
    return this.backendService.getById<StaticLookupAsync>(StaticLookupAsync, lookupId).pipe(
      map(result => {
        const lookupItems = this.prependBlankIfRequired(result.items, insertBlank);
        return _.sortBy(lookupItems, item => item.value);
      }));
  }

  /**
   *
   * @param lookupType e.g.
   *   - lookups,
   *   - lookup-row
   * @param id a GUID string
   * @param insertBlank
   */
  public getByTypeAndIdAsync(lookupType: string, id: string, insertBlank?: boolean): Observable<LookupItem[]> {
    return this.backendService.getByIdArray<StaticLookupAsync>(StaticLookupAsync, [lookupType, id]).pipe(
      map(result => this.prependBlankIfRequired(result.items, insertBlank)));
  }

  public convertApiLookup(values: { [key: string]: string }): LookupItem[] {
    const result: LookupItem[] = [];

    for (const key in values) {
      if (values.hasOwnProperty(key)) {
        const element = values[key];
        result.push(new LookupItem(key, element));
      }
    }

    return _.sortBy(result, (item: LookupItem) => item.value);
  }

  private prependBlankIfRequired(lookupItems: LookupItem[] = [], insertBlank?: boolean): LookupItem[] {
    if (insertBlank) {
      const newLookupItems = [new LookupItem('', this.i18NextPipe.transform('None'))];
      return newLookupItems.concat(lookupItems);
    }

    return lookupItems;
  }

  public getPaginatedLookupDisplayValues(params: ObjectDrawerParameters, pageNumber: number, pageSize: number): Observable<LookupDisplayValue[]> {
    return this.backendPaginationService.paginateWithParameters<LookupDisplayValue>(LookupDisplayValue, params, pageNumber, pageSize);
  }

  public getAllLookupDisplayValuesById(lookupId: string): Observable<LookupDisplayValue[]> {
    return this.backendService.getAllById<LookupDisplayValue>(
      LookupDisplayValue,
      lookupId,
      CONFIG.apiRoutes.lookupDisplayValue,
    );
  }
  

  public createLookupDisplayValuesParams(searchTerm: string, sourceLookupId: string): ObjectDrawerParameters {
    const params = new ObjectDrawerParameters();
    params.searchText = searchTerm;
    params.filters.push(this.createLookupFilter(sourceLookupId));
    return params;
  }

  private createLookupFilter(sourceLookupId: string): ObjectDrawerFilter {
    const lookupIdFilter = new ObjectDrawerFilter();
    lookupIdFilter.valueType = ObjectDrawerFilterValueType.Guid;
    lookupIdFilter.value = sourceLookupId;
    const nameof = <T>(name: keyof T) => name;
    const propertyName = nameof<LookupDisplayValue>('lookupId');
    lookupIdFilter.propertyName = propertyName.replace(propertyName[0], propertyName[0].toUpperCase());
    return lookupIdFilter;
  }
}
